import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '..';

import './TaskTracker.scss';

/**
 * TaskTracker - A reusable timeline component for tracking tasks/items
 * @param {Array} items - Array of items to track
 * @param {Function} renderContent - Render prop for custom content card
 * @param {Function} getStatus - Function to determine item status (returns 'completed' | 'in_progress' | 'pending')
 * @param {Function} getKey - Function to get unique key for each item
 * @param {string} title - Optional header title
 * @param {boolean} showProgress - Whether to show progress bar header
 * @param {string} emptyTitle - Title for empty state
 * @param {string} emptyMessage - Message for empty state
 * @param {Function} onAddBetween - Callback for adding item between existing items (index) => void
 * @param {boolean} showLastAddButton - Whether to show Add button after last item (default: true)
 */
export const TaskTracker = ({
  items = [],
  renderContent,
  getStatus,
  getKey = (item, index) => item.id || index,
  title = 'Progress',
  showProgress = true,
  emptyTitle = 'No Tasks',
  emptyMessage = 'No tasks to display.',
  startDate = '',
  onAddBetween, // Callback for adding item between existing items (index) => void
  showLastAddButton = true, // Control whether to show Add button after last item
  showAddButton = true,
  lastNotAddText = 'Add last node',
}) => {
  // Process items with derived status
  const processedItems = items.map((item, index) => {
    let derivedStatus;

    if (getStatus) {
      // Use custom status function if provided
      derivedStatus = getStatus(item, index, items);
    } else {
      // If startDate is empty, all items should be pending
      if (!startDate) {
        derivedStatus = 'pending';
      } else {
        // Default status logic based on is_completed field
        if (item.is_completed) {
          derivedStatus = 'completed';
        } else {
          const prevItem = items[index - 1];
          if (!prevItem || prevItem.is_completed) {
            derivedStatus = 'in_progress';
          } else {
            derivedStatus = 'pending';
          }
        }
      }
    }

    return { ...item, derivedStatus };
  });

  const completedCount = processedItems.filter(
    (item) => item.derivedStatus === 'completed'
  ).length;
  const progressPercent =
    items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0;

  if (!items || items.length === 0) {
    return (
      <div className='task-tracker task-tracker__empty'>
        <div className='task-tracker__empty-content'>
          <Icon name='Document' />
          <h5>{emptyTitle}</h5>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='task-tracker'>
      {showProgress && (
        <div className='task-tracker__header'>
          <div className='task-tracker__header-info'>
            <h3>{title}</h3>
            <span className='task-tracker__progress-text'>
              {progressPercent}% Complete
            </span>
          </div>
          <div className='task-tracker__progress-bar'>
            <div
              className='task-tracker__progress-fill'
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      <div className='task-tracker__timeline'>
        {processedItems.map((item, index) => (
          <div
            key={getKey(item, index)}
            className={`task-tracker__item task-tracker__item--${item.derivedStatus}`}
          >
            <div className='task-tracker__marker'>
              <div className='task-tracker__marker-dot'>
                {item.derivedStatus === 'completed' && (
                  <Icon name='Check' size='12px' />
                )}
                {item.derivedStatus === 'in_progress' && (
                  <div className='task-tracker__pulse-dot' />
                )}
              </div>
              <div className='task-tracker__marker-line' />
            </div>

            <div className='task-tracker__content'>
              {renderContent ? (
                renderContent(item, index, item.derivedStatus)
              ) : (
                <div>Render content not provided</div>
              )}
            </div>

            {/* Add button between items */}
            {showAddButton &&
              onAddBetween &&
              index < processedItems.length - 1 && (
                <button
                  className='task-tracker__add-between-btn'
                  onClick={() => onAddBetween(index + 1)}
                  title='Add milestone here'
                >
                  <Icon name='Add_Plus' size='12px' />
                </button>
              )}
          </div>
        ))}

        {/* Add button after last item with line connection */}
        {showAddButton &&
          onAddBetween &&
          showLastAddButton &&
          processedItems.length > 0 && (
            <div className='task-tracker__item task-tracker__item--last-add'>
              <div className='task-tracker__marker'>
                <div className='task-tracker__marker-line' />
                <div className='task-tracker__marker-dot'>
                  <button
                    className='task-tracker__add-last-btn'
                    onClick={() => onAddBetween(processedItems.length)}
                    title='Add milestone at the end'
                  >
                    <Icon name='Add_Plus' size='12px' />
                  </button>
                </div>
              </div>
              <div className='task-tracker__content'>
                <div className='task-tracker__add-last-placeholder'>
                  <span>{lastNotAddText}</span>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

TaskTracker.propTypes = {
  items: PropTypes.array.isRequired,
  renderContent: PropTypes.func,
  getStatus: PropTypes.func,
  getKey: PropTypes.func,
  title: PropTypes.string,
  showProgress: PropTypes.bool,
  emptyTitle: PropTypes.string,
  emptyMessage: PropTypes.string,
  startDate: PropTypes.string,
  onAddBetween: PropTypes.func,
  showLastAddButton: PropTypes.bool,
  showAddButton: PropTypes.bool,
  lastNotAddText: PropTypes.string,
};
